import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import {
  fetchCalendlySlots,
  postCalendlyCheckSlot,
} from '../api/voice-bot-client'
import type { CalendlySlot } from '../types'

// ============================================
// Calendly slot tool handlers (Wave 3b)
// ============================================
// Lifted from VoiceBotPanel.handleFunctionCall:
//   - runGetAvailableSlots: get_available_slots — fetches and formats
//     ranges/slots, populates availableSlots state.
//   - runCheckSpecificSlot: check_specific_slot — verifies a chosen
//     slot and returns the bot prompt for the booking-collection step,
//     branching on contactFormTiming.
// Behavior preserved 1:1 with the original inline handlers.

interface SlotsApiDay {
  dayFormatted: string
  ranges: Array<{ formattedRange: string }>
}

interface SlotsApiResponse {
  success?: boolean
  error?: string
  message?: string
  calendlyEnabled?: boolean
  slots?: CalendlySlot[]
  timeRanges?: SlotsApiDay[]
}

interface CheckSlotApiResponse {
  success?: boolean
  available?: boolean
  error?: string
  slot?: { message?: string }
  alternativeSlots?: Array<{ formattedTime: string }>
}

export interface RunGetAvailableSlotsOptions {
  args: Record<string, unknown>
  customerId: string | undefined
  setAvailableSlots: Dispatch<SetStateAction<CalendlySlot[]>>
}

export const runGetAvailableSlots = async (
  opts: RunGetAvailableSlotsOptions
): Promise<string> => {
  const { args, customerId, setAvailableSlots } = opts
  const slotsResponse = await fetchCalendlySlots(
    args.timePreference as string | undefined,
    customerId,
    args.preferredDate as string | undefined
  )
  if (!slotsResponse.ok) {
    console.error('📅 calendly-slots HTTP error:', slotsResponse.status)
    return 'SYSTEM ERROR: Could not check availability right now. Apologize and ask customer to try again in a moment.'
  }
  let slotsData: SlotsApiResponse
  try {
    slotsData = await slotsResponse.json()
  } catch (parseErr) {
    console.error('📅 calendly-slots JSON parse error:', parseErr)
    return 'SYSTEM ERROR: Could not check availability right now. Apologize and ask customer to try again in a moment.'
  }
  if (slotsData.error && !slotsData.success) {
    console.error(
      '📅 calendly-slots API error:',
      slotsData.error,
      slotsData.message
    )
    if (slotsData.calendlyEnabled === false) {
      return 'SYSTEM ERROR: Meeting scheduling is not configured for this account. Apologize and offer to take their contact details instead.'
    }
    return 'SYSTEM ERROR: Could not check availability right now. Apologize and ask customer to try again in a moment.'
  }
  if (
    slotsData.success &&
    slotsData.timeRanges &&
    slotsData.timeRanges.length > 0
  ) {
    setAvailableSlots(slotsData.slots || [])
    const rangesText = slotsData.timeRanges
      .map((day) => {
        const rangesForDay = day.ranges
          .map((r) => r.formattedRange)
          .join(' and ')
        return `${day.dayFormatted}: ${rangesForDay}`
      })
      .join('. ')
    return `AVAILABLE: ${rangesText}. Ask customer when works for them. Reply in the user's selected conversation language.`
  }
  if (slotsData.success && slotsData.slots && slotsData.slots.length > 0) {
    setAvailableSlots(slotsData.slots)
    const slotsText = slotsData.slots
      .slice(0, 2)
      .map((s) => s.formatted)
      .join(' or ')
    return `AVAILABLE: ${slotsText}. Ask customer preference. Reply in the user's selected conversation language.`
  }
  return 'NO SLOTS AVAILABLE for the requested date. Ask customer to suggest a different day.'
}

export interface RunCheckSpecificSlotOptions {
  args: Record<string, unknown>
  customerId: string | undefined
  configRef: MutableRefObject<{ contactFormTiming?: string }>
}

export const runCheckSpecificSlot = async (
  opts: RunCheckSpecificSlotOptions
): Promise<string> => {
  const { args, customerId, configRef } = opts
  console.log('🔍 Frontend calling check_specific_slot with args:', args)
  const checkResponse = await postCalendlyCheckSlot({ ...args, customerId })
  if (!checkResponse.ok) {
    console.error('📅 calendly-check-slot HTTP error:', checkResponse.status)
    return 'SYSTEM ERROR: Could not check that time slot right now. Apologize and ask customer to try again.'
  }
  let checkData: CheckSlotApiResponse
  try {
    checkData = await checkResponse.json()
  } catch (parseErr) {
    console.error('📅 calendly-check-slot JSON parse error:', parseErr)
    return 'SYSTEM ERROR: Could not check that time slot right now. Apologize and ask customer to try again.'
  }
  console.log('🔍 Frontend received response:', checkData)
  if (checkData.success) {
    if (checkData.available) {
      console.log(
        '✅ Frontend: Slot IS available, returning:',
        checkData.slot?.message
      )
      return (configRef.current.contactFormTiming || 'none') ===
        'before_scheduling'
        ? 'AVAILABLE! If the customer wants this time, call book_meeting immediately. Pass the customer name in customerName if you already know it; otherwise leave it blank — the popup will collect name, email and phone. Do not ask for email or phone yourself.'
        : 'AVAILABLE! Ask for customer name to book.'
    }
    console.log('❌ Frontend: Slot NOT available')
    const alternatives = (checkData.alternativeSlots || [])
      .slice(0, 2)
      .map((s) => s.formattedTime)
      .join(' or ')
    const result = alternatives
      ? `NOT AVAILABLE. Alternatives: ${alternatives}. Offer these times. Reply in the user's selected conversation language.`
      : 'NOT AVAILABLE for that time. Ask customer to suggest a different time. Reply in the user\'s selected conversation language.'
    console.log('❌ Frontend: Sending result to bot:', result)
    return result
  }
  console.error('📅 check-slot API error:', checkData.error)
  return 'SYSTEM ERROR: Could not check that time slot. Apologize and ask customer to suggest another time.'
}
